import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApiToken } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head, Measure } from "@/ui/frame/Page";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { OrgSettingsScreens } from "@/features/org/screens";
import { Dialog } from "@/ui/Overlay";
import { Field, Input } from "@/ui/Field";
import { Choice } from "@/ui/Choice";
import { Chip } from "@/ui/Chip";
import { FormError, FormNotice } from "@/features/auth/AuthCard";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";
import { CopyField } from "@/ui/Copy";
export const Route = createFileRoute("/_app/o/$orgId/tokens")({
  component: TokensScreen,
});

const SCOPES = [
  { value: "read", label: "Read", description: "View services, databases, logs and metrics." },
  { value: "deploy", label: "Deploy", description: "Everything in read, plus builds, deployments and variables." },
  { value: "admin", label: "Admin", description: "Everything in deploy, plus creating and deleting projects." },
];

const EXPIRY_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "0", label: "No expiry" },
];

function TokensScreen() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const tokens = useQuery({
    queryKey: ["tokens", orgId],
    queryFn: ({ signal }) =>
      api.get<{ tokens: ApiToken[] }>(`/organisations/${orgId}/tokens`, undefined, signal),
  });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => api.delete<void>(`/organisations/${orgId}/tokens/${tokenId}`),
    onSuccess: async () => {
      toast.success("Token revoked", "It stopped working immediately.");
      await queryClient.invalidateQueries({ queryKey: ["tokens", orgId] });
    },
    onError: toast.error,
  });

  const live = tokens.data?.tokens.filter((token) => !token.revoked_at) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Measure>
        <Head
          title="API tokens"
          description="Tokens authenticate the CLI and your automation. A token can never do more than the person who created it."
          actions={
            <Button tone="primary" onClick={() => setCreating(true)}>
              New token
            </Button>
          }
          screens={<OrgSettingsScreens />}
        />
        <div className="flex flex-col gap-8">
      <CreateTokenDialog orgId={orgId} open={creating} onOpenChange={setCreating} />

      <Panel padded={false}>
        {tokens.isLoading && <TableSkeleton rows={3} columns={5} />}
        {tokens.isError && (
          <div className="px-5 py-4">
            <ErrorState error={tokens.error} onRetry={() => void tokens.refetch()} />
          </div>
        )}

        {tokens.data && live.length === 0 && (
          /* No button. The page header already carries "New token" and it
             stays there once a token exists, so repeating it here put two
             identical primary actions seventy pixels apart on an empty
             page. */
          <EmptyState
            title="No API tokens"
            description="Create one to use the Drumlin CLI or to deploy from your own pipeline. A token can never do more than the person who created it."
          />
        )}

        {live.length > 0 && (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Token</Th>
                  <Th>Scope</Th>
                  <Th>Last used</Th>
                  <Th>Expires</Th>
                  <Th align="right" quiet>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {live.map((token) => (
                  <Tr key={token.id}>
                    <Td className="font-medium">{token.name}</Td>
                    <Td>
                      <code className="mono text-[13px] text-[var(--text-secondary)]">
                        {token.prefix}…
                      </code>
                    </Td>
                    <Td>
                      <Chip tone={token.scope === "admin" ? "warn" : undefined}>{token.scope}</Chip>
                    </Td>
                    <Td className="text-[var(--text-secondary)]">
                      {token.last_used_at ? relativeTime(token.last_used_at) : "never"}
                    </Td>
                    <Td className="text-[var(--text-secondary)]">
                      {token.expires_at ? (
                        <span title={absoluteTime(token.expires_at)}>{relativeTime(token.expires_at)}</span>
                      ) : (
                        <Chip tone="warn">No expiry</Chip>
                      )}
                    </Td>
                    <Td actions>
                      <Button
                        size="dense"
                        tone="ghost"
                        busy={revoke.isPending && revoke.variables === token.id}
                        onClick={() => revoke.mutate(token.id)}
                      >
                        Revoke
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
        </div>
      </Measure>
    </div>
  );
}

function CreateTokenDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [scope, setScope] = useState("deploy");
  const [expiry, setExpiry] = useState("90");
  const [secret, setSecret] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ token: ApiToken; secret: string }>(`/organisations/${orgId}/tokens`, {
        name,
        scope,
        expires_in_days: Number(expiry),
      }),
    onSuccess: async (result) => {
      setSecret(result.secret);
      await queryClient.invalidateQueries({ queryKey: ["tokens", orgId] });
    },
  });

  function close() {
    onOpenChange(false);
    // Reset only after the dialog has closed, so the secret does not flash away
    // while the card is still visible.
    window.setTimeout(() => {
      setSecret(null);
      setName("");
      create.reset();
    }, 200);
  }

  if (secret) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => !next && close()}
        title="Copy your token now"
        description="This is the only time it is shown. If you lose it, revoke it and create another."
        footer={
          <Button tone="primary" onClick={close}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <CopyField value={secret} label="API token" />
          <FormNotice title="Store it somewhere safe.">
            Treat it like a password: anyone holding it can act as you, within this token&rsquo;s
            scope, until it is revoked.
          </FormNotice>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="New API token"
      footer={
        <>
          <Button tone="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={create.isPending}
            disabled={name.trim().length === 0}
            onClick={() => create.mutate()}
          >
            Create token
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Name" required hint="Something you will recognise in six months, such as “CI deploys”.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              autoFocus
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Scope" required hint={SCOPES.find((s) => s.value === scope)?.description}>
          {({ id, describedBy }) => (
            <Choice
              id={id}
              value={scope}
              onValueChange={setScope}
              options={SCOPES}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field
          label="Expires"
          hint={expiry === "0" ? "A token without an expiry stays valid until you revoke it." : undefined}
        >
          {({ id, describedBy }) => (
            <Choice
              id={id}
              value={expiry}
              onValueChange={setExpiry}
              options={EXPIRY_OPTIONS}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        {create.error ? <FormError error={create.error} /> : null}
      </form>
    </Dialog>
  );
}
